import { useEffect, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { REGISTER_NMI_DEVICE, UPDATE_TERMINAL, DELETE_TERMINAL, DEREGISTER_NMI_DEVICE } from "src/constants/mutations";
import { GET_TERMINALS_BY_STORE } from "src/constants/queries";
import { successToast, failureToast } from "src/utils/toasts";
import { BtnLoader } from "src/Loader";
import { Terminal, TerminalRegistrationStatus } from "src/interfaces/terminal.interface";
import FormRowVertical from "src/components/common/FormRowVertical";
import Input, { InputTypeEnum } from "src/components/common/Input";
import CustomSelect from "src/components/common/CustomSelect";
import Button from "src/components/common/Button";

const TerminalSettings = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingTerminals, setIsLoadingTerminals] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  
  // Registration form state
  const [registrationCode, setRegistrationCode] = useState("");
  const [deviceNickname, setDeviceNickname] = useState("");
  const [terminalType, setTerminalType] = useState<"COUNTERTOP" | "HANDHELD">("COUNTERTOP");

  useEffect(() => {
    fetchTerminals();
  }, [hasRestaurant?.correlationId]);

  const fetchTerminals = async () => {
    if (!hasRestaurant?.correlationId) return;
    
    setIsLoadingTerminals(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_TERMINALS_BY_STORE,
        variables: {
          storeCorrelationId: hasRestaurant.correlationId,
        },
      });

      if (data?.errors?.length) {
        throw data.errors;
      }

      setTerminals(data?.data?.getTerminalsByStore || []);
    } catch (error) {
      console.error("Error fetching terminals:", error);
      failureToast("Failed to load terminals");
    } finally {
      setIsLoadingTerminals(false);
    }
  };

  const handleRegisterDevice = async () => {
    if (!hasRestaurant?.correlationId || !registrationCode || !deviceNickname) {
      failureToast("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: REGISTER_NMI_DEVICE,
        variables: {
          registerTerminalInput: {
            storeCorrelationId: hasRestaurant.correlationId,
            registrationCode: registrationCode.trim(),
            deviceNickname: deviceNickname.trim(),
            terminalType: terminalType,
          },
        },
      });

      if (data?.errors?.length) {
        throw data.errors;
      }

      successToast("Device registered successfully!");
      setRegistrationCode("");
      setDeviceNickname("");
      setTerminalType("COUNTERTOP");
      setShowRegistrationForm(false);
      await fetchTerminals();
    } catch (error: any) {
      console.error("Error registering device:", error);
      const errorMessage = error?.message || error?.[0]?.message || "Failed to register device";
      failureToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeregisterTerminal = async (terminalCorrelationId: string, providerTerminalId?: string) => {
    if (!window.confirm("Are you sure you want to deregister this terminal from NMI? This will also remove it from your system.")) {
      return;
    }

    setLoading(true);
    try {
      // Only deregister if it's an NMI device with providerTerminalId
      if (providerTerminalId) {
        const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
          query: DEREGISTER_NMI_DEVICE,
          variables: {
            terminalCorrelationId: terminalCorrelationId,
          },
        });

        if (data?.errors?.length) {
          throw data.errors;
        }

        successToast("Terminal deregistered from NMI successfully!");
      } else {
        // Fallback to regular delete if not NMI or no providerTerminalId
        await handleDeleteTerminal(terminalCorrelationId);
        return;
      }
      
      await fetchTerminals();
    } catch (error: any) {
      console.error("Error deregistering terminal:", error);
      const errorMessage = error?.message || error?.[0]?.message || "Failed to deregister terminal";
      failureToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTerminal = async (terminalCorrelationId: string) => {
    if (!window.confirm("Are you sure you want to delete this terminal?")) {
      return;
    }

    setLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: DELETE_TERMINAL,
        variables: {
          terminalCorrelationId: terminalCorrelationId,
        },
      });

      if (data?.errors?.length) {
        throw data.errors;
      }

      successToast("Terminal deleted successfully!");
      await fetchTerminals();
    } catch (error: any) {
      console.error("Error deleting terminal:", error);
      const errorMessage = error?.message || error?.[0]?.message || "Failed to delete terminal";
      failureToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const terminalTypeOptions = [
    { value: "COUNTERTOP", label: "Countertop" },
    { value: "HANDHELD", label: "Handheld" },
  ];

  if (isLoadingTerminals) {
    return (
      <div className="w-full max-w-[760px] flex justify-center items-center py-12">
        <BtnLoader />
      </div>
    );
  }

  return (
    <div className="w-full max-w-[760px]">
      <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em]">Payment Terminals</h2>
          <button
            type="button"
            onClick={() => setShowRegistrationForm(!showRegistrationForm)}
            className="text-[13px] font-medium text-[#6b7280] bg-transparent border-none cursor-pointer hover:text-[#111827] focus:outline-none"
          >
            {showRegistrationForm ? "Cancel" : "Register New Device"}
          </button>
        </div>

        {showRegistrationForm && (
          <div className="mb-6 p-4 rounded-[12px] border border-[#e5e7eb] bg-[#f9fafb]">
            <h3 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-4">Register NMI Device</h3>
            <FormRowVertical label="Registration Code" className="mt-4">
              <Input
                name="registrationCode"
                type={InputTypeEnum.TEXT}
                value={registrationCode}
                onChange={(e) => setRegistrationCode(e.target.value)}
                placeholder="Enter registration code (e.g., T00001)"
              />
            </FormRowVertical>
            <FormRowVertical label="Device Nickname" className="mt-4">
              <Input
                name="deviceNickname"
                type={InputTypeEnum.TEXT}
                value={deviceNickname}
                onChange={(e) => setDeviceNickname(e.target.value)}
                placeholder="Enter device nickname"
              />
            </FormRowVertical>
            <FormRowVertical label="Terminal Type" className="mt-4">
              <CustomSelect
                options={terminalTypeOptions}
                value={terminalType}
                onChange={(value) => setTerminalType(value as "COUNTERTOP" | "HANDHELD")}
                placeholder="Select terminal type"
              />
            </FormRowVertical>
            <div className="flex justify-end mt-6">
              {loading ? (
                <BtnLoader />
              ) : (
                <button
                  type="button"
                  onClick={handleRegisterDevice}
                  disabled={!registrationCode || !deviceNickname || loading}
                  className={`px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 ${
                    (!registrationCode || !deviceNickname || loading) ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Register Device
                </button>
              )}
            </div>
          </div>
        )}

        <div className="mt-6">
          {terminals.length === 0 ? (
            <p className="text-center py-6 text-[14px] text-[#6b7280]">
              No terminals registered. Click &quot;Register New Device&quot; to add one.
            </p>
          ) : (
            <div className="space-y-3">
              {terminals.map((terminal) => (
                <div
                  key={terminal.correlationId}
                  className="rounded-[12px] border border-[#e5e7eb] p-4 flex justify-between items-start"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2">
                      <h3 className="text-[14px] font-medium text-[#111827]">{terminal.name}</h3>
                      <span className={`px-2 py-1 rounded text-xs ${
                        terminal.active ? "bg-green-100 text-green-800" : "bg-[#f3f4f6] text-[#6b7280]"
                      }`}>
                        {terminal.active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="mt-2 text-[14px] text-[#4b5563] space-y-1">
                      <div>
                        <span className="font-medium text-[#111827]">Type:</span> {terminal.terminalType}
                      </div>
                      <div>
                        <span className="font-medium text-[#111827]">Provider:</span> {terminal.provider}
                      </div>
                      {terminal.serialNumber && (
                        <div>
                          <span className="font-medium text-[#111827]">Serial Number:</span> {terminal.serialNumber}
                        </div>
                      )}
                      {terminal.providerTerminalId && (
                        <div>
                          <span className="font-medium text-[#111827]">Device ID:</span> {terminal.providerTerminalId}
                        </div>
                      )}
                      {terminal.lastSeenAt && (
                        <div>
                          <span className="font-medium text-[#252525]">Last Seen:</span>{" "}
                          {new Date(terminal.lastSeenAt).toLocaleString()}
                        </div>
                      )}
                      {terminal.registrationStatus && (
                        <div>
                          <span className="font-medium text-[#252525]">Status:</span>{" "}
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            terminal.registrationStatus === "REGISTERED" 
                              ? "bg-green-100 text-green-800"
                              : terminal.registrationStatus === "DEREGISTERED"
                              ? "bg-gray-100 text-gray-800"
                              : terminal.registrationStatus === "DEREGISTRATION_FAILED" || terminal.registrationStatus === "REGISTRATION_FAILED"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}>
                            {terminal.registrationStatus.replace(/_/g, " ")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {(() => {
                      // Show buttons based on terminal type and status
                      const isNMI = terminal.provider === "NMI";
                      const isRegistered = terminal.registrationStatus === "REGISTERED";
                      const isActive = terminal.active;
                      const hasProviderTerminalId = !!terminal.providerTerminalId;
                      const isDeregistrationFailed = terminal.registrationStatus === "DEREGISTRATION_FAILED";

                      // For active NMI terminals that are registered: show only "Deregister"
                      if (isNMI && isRegistered && isActive && hasProviderTerminalId) {
                        return (
                          <Button
                            onClick={() => handleDeregisterTerminal(terminal.correlationId, terminal.providerTerminalId)}
                            disabled={loading}
                            variant="secondary"
                            size="sm"
                          >
                            Deregister
                          </Button>
                        );
                      }

                      // For non-NMI terminals, inactive terminals, or deregistration failed: show only "Delete"
                      // This includes:
                      // - Non-NMI terminals (STRIPE or manually created)
                      // - Inactive terminals (already soft-deleted)
                      // - NMI terminals with deregistration failed status (need cleanup)
                      // - NMI terminals that are deregistered but somehow still active
                      if (!isActive || !isNMI || !isRegistered || !hasProviderTerminalId || isDeregistrationFailed) {
                        return (
                          <button
                            type="button"
                            onClick={() => handleDeleteTerminal(terminal.correlationId)}
                            disabled={loading}
                            className="text-[13px] font-medium text-[#6b7280] hover:text-[#111827] disabled:opacity-50"
                          >
                            Delete
                          </button>
                        );
                      }

                      // Fallback: show nothing if none of the above conditions match
                      return null;
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TerminalSettings;

